/**
 * シリーズ選択画面の動的装飾（TK-032、FR-023、ADR-017、`04-architecture.md` series-select）。
 *
 * ビルドが生成した静的なシリーズ一覧（`src/build/pages.ts` renderSeriesSelectPage）に対し、
 * - **New マーク**: 埋め込まれたリリース日（`data-released-at`）とクライアントの現在時刻の差が
 *   30 日以内なら表示する。クライアント側判定なので、期限切れは再デプロイなしで消える。
 *   ただし 1 ステージでもクリアしたシリーズ（進捗の `done ≥ 1` またはクリア済み）には
 *   期間内でも付けない（TK-061。未着手の合図として使う）
 * - **クリアマーク・続きから**: `localStorage` の進捗（`zp.progress`、`05` §4）から表示・遷移先を決める
 *
 * いずれも JS による装飾であり、JS 無効・`localStorage` 不可・進捗破損でも一覧とリンクは
 * 静的 HTML として機能する（プログレッシブエンハンスメント）。判定は純関数
 * `seriesEntryDecoration` に置き、DOM への適用だけを `initSeriesSelect` が受け持つ。
 */

import type { ProgressMap, SeriesProgress } from './progress';

/** New マークの表示期間（リリース日から 30 日。FR-023）。 */
export const NEW_MARK_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** シリーズ一覧エントリ 1 件に載せる装飾。 */
export interface SeriesEntryDecoration {
  /** New マークを表示するか。 */
  readonly isNew: boolean;
  /** クリアマークを表示するか。 */
  readonly cleared: boolean;
  /** 「続きから」の遷移先。無ければ表示しない。 */
  readonly resume?: string;
}

/**
 * リリース日が New マークの表示期間内か。
 *
 * 解釈できない日付は false（マークなし）。未来の日付（クライアントの時計の遅れ）は
 * 期間内として扱う — 時計の狂いで New が消える方向より無害（ADR-017 の受容と同じ理屈）。
 */
export function isNewRelease(releasedAt: string | undefined, now: number): boolean {
  if (releasedAt === undefined) return false;
  const released = Date.parse(releasedAt);
  if (Number.isNaN(released)) return false;
  return now - released <= NEW_MARK_WINDOW_MS;
}

/**
 * 再開ポイントとして採用できる URL か。
 *
 * `zp.progress` は書き換え可能な `localStorage` 由来のため、ステージ URL の形
 * （`/p/…/`。`06-api-design.md` URL 空間）以外は捨てる。`javascript:` 等のスキームや
 * 外部 URL が `href` に載ることを構造的に防ぐ。
 */
function resumableUrl(resume: string | undefined): string | undefined {
  if (resume === undefined) return undefined;
  return /^\/p\/[0-9a-f-]+\/$/.test(resume) ? resume : undefined;
}

/** エントリ 1 件の装飾を決める。時刻は引数で受け、テストで固定できるようにする。 */
export function seriesEntryDecoration(
  releasedAt: string | undefined,
  progress: SeriesProgress | undefined,
  now: number,
): SeriesEntryDecoration {
  const cleared = progress?.cleared ?? false;
  const resume = cleared ? undefined : resumableUrl(progress?.resume);
  // New は「未着手」の合図。1 ステージでもクリアしたシリーズには期間内でも付けない
  // （TK-061。既に遊んでいる本人に新着を知らせる意味がないため）。
  const played = cleared || (progress?.done ?? 0) > 0;
  return {
    isNew: !played && isNewRelease(releasedAt, now),
    cleared,
    ...(resume !== undefined ? { resume } : {}),
  };
}

/** マーク要素（`<span>`）を作る。 */
function mark(doc: Document, kind: 'new' | 'cleared', label: string): HTMLSpanElement {
  const span = doc.createElement('span');
  span.className = `series-mark series-mark--${kind}`;
  span.textContent = label;
  return span;
}

/**
 * 一覧の全エントリへ装飾を適用する。
 *
 * @param root 選択画面のルート（`.series-entry[data-series]` を含む）
 * @param progress 現在の進捗。読めない・壊れている場合は空の Map を渡す（素の一覧のまま）
 * @param now 現在時刻（epoch ミリ秒）
 */
export function initSeriesSelect(root: HTMLElement, progress: ProgressMap, now: number): void {
  const doc = root.ownerDocument;
  for (const entry of root.querySelectorAll<HTMLElement>('.series-entry[data-series]')) {
    const series = entry.dataset.series ?? '';
    const decoration = seriesEntryDecoration(entry.dataset.releasedAt, progress[series], now);
    const link = entry.querySelector<HTMLElement>('.series-link') ?? entry;
    if (decoration.isNew) link.append(mark(doc, 'new', 'New'));
    if (decoration.cleared) link.append(mark(doc, 'cleared', 'クリア済み'));
    if (decoration.resume !== undefined) {
      const resume = doc.createElement('a');
      resume.className = 'series-resume';
      resume.href = decoration.resume;
      resume.textContent = '続きから';
      entry.append(resume);
    }
  }
}
